import { useDebounceEffect, useLatest } from 'ahooks';
import React from 'react';
import { Box } from '@apia/theme';
import actions from '../../../../api/apiaApi/actions';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import parameters from '../../../../config/parameters';
import { arrayOrArray } from '../../../../utils/util';
import Tree from '../../../common/generic/tree/Tree';
import TreeDataController from '../../../common/generic/tree/TreeDataController';
import { TDataNode, TId, TTreeProps } from '../../../common/generic/tree/types';
import LeafsAsLinkRenderer from './LeafsAsLinkRenderer';
import { TSearchMenuResponse } from './types';

export interface ISearchResult {
  searchString: string;
}

export type TSearchNodeProps = {
  isFavorite?: boolean;
  onFavoriteClick?: () => unknown;
  onClick?: (ev: React.KeyboardEvent | React.MouseEvent) => unknown;
  showLabel?: string;
};

const SearchResult = ({
  onArrowUpOnFirstElement,
  searchString,
}: ISearchResult & Pick<TTreeProps, 'onArrowUpOnFirstElement'>) => {
  const handler = React.useRef<TreeDataController<TSearchNodeProps>>(
    {} as TreeDataController<TSearchNodeProps>,
  );

  const actualSearchString = useLatest(searchString);

  const runSearch = React.useCallback(
    async (whatToSearch: string) => {
      if (whatToSearch === '') return;
      const result = await ApiaApi.post<TSearchMenuResponse>(
        makeApiaUrl({
          ajaxUrl: actions.menu.ajaxUrl,
          action: actions.menu.filterFunctionality,
          search: whatToSearch,
        }),
      );

      const functionalities = arrayOrArray(result?.data?.functionality);
      const existingNodes = handler.current.getNodesIds();

      handler.current.batchInit();

      let prevId: TId;

      existingNodes.forEach((currentId) => {
        if (!functionalities.find((current) => current.fncId === currentId))
          handler.current.remove(currentId);
      });

      functionalities.forEach((current) => {
        const nodeProps = {
          isFavorite: current.isFav,
          onFavoriteClick: async () => {
            await ApiaApi.post(
              makeApiaUrl({
                ajaxUrl: actions.common.toggleFavoriteAjaxUrl,
                action: actions.common.toggleFavorite,
                favFncId: current.fncId,
              }),
            );
            void runSearch(actualSearchString.current);
          },
          onClick: (ev: React.MouseEvent | React.KeyboardEvent) =>
            window.tabsController.openTabByUrl(
              current.title,
              current.url.replaceAll('&amp;', '&'),
              {
                funcId: current.fncId,
                keepMenuOpen: ev.ctrlKey,
              },
            ),
          showLabel: current.show,
        };
        if (!handler.current.propsStore.getFieldProps(current.fncId))
          handler.current.append({
            id: current.fncId,
            isLeaf: true,
            label: current.title,
            labelRenderer: LeafsAsLinkRenderer,
            nodeProps,
            parentId: 'root',
          });
        else {
          if (prevId !== undefined) {
            handler.current.move(current.fncId, 'root', { after: prevId });
          }
          handler.current.propsStore.updateField(current.fncId, {
            nodeProps,
          });
          prevId = current.fncId;
        }
      });
      handler.current.batchFinish();
      const firstChildId =
        handler.current.propsStore.getFieldProps('root').children[0];
      handler.current.setFocusedNode(firstChildId);
    },
    [actualSearchString],
  );

  useDebounceEffect(
    () => {
      void runSearch(searchString);
    },
    [searchString],
    { trailing: true, wait: parameters.timing.keyboardFilterDebounceTime },
  );

  return (
    <Box
      sx={React.useMemo(
        () => ({
          display: searchString === '' ? 'none' : undefined,
        }),
        [searchString],
      )}
      className={`menu__searchResult ${searchString === '' ? '' : 'active'}`}
    >
      <Tree<TSearchNodeProps>
        disableSelection
        focusGetter={React.useCallback((liElement: HTMLElement) => {
          return (liElement.querySelector(
            ':scope > .tree__nodeItemLabel .menu__openTask',
          ) ||
            liElement.querySelector(
              ':scope > .tree__nodeItemLabel',
            )) as HTMLElement;
        }, [])}
        getHandler={React.useCallback(
          (h: TreeDataController<TSearchNodeProps>) => {
            handler.current = h;
          },
          [],
        )}
        label={window.LBL_SEARCH_MENU}
        name="searchResults"
        onArrowUpOnFirstElement={onArrowUpOnFirstElement}
        onNodeClick={React.useCallback(
          (
            ev: React.KeyboardEvent | React.MouseEvent,
            node: TDataNode<TSearchNodeProps>,
          ) => {
            node.nodeProps?.onClick?.(ev);
          },
          [],
        )}
      />
    </Box>
  );
};

export default SearchResult;
